import api from '../../API';
import { useLoaderData, useRevalidator } from 'react-router-dom';
import { useEffect, useRef } from 'react';
import WsiCard from '../../widgets/WsiCard';
import { Centered } from '../../layouts/Base';
import { Link } from 'react-router-dom';
import styles from './styles.module.css';
import { useAuth } from '../../providers/AuthContext';
import { queryClient } from '../../providers/AuthContext';
import { extractData, fetchApiQuery } from '../../utils/apiResponse';

function Folder(){
    const { user, pending } = useAuth();
    const revalidator = useRevalidator();
    const hasRevalidated = useRef(false);

    // The loader will access current settings 
    const {ok, data, meta, items, folders, breadcrumb = [], error} = useLoaderData();

    // Revalidate loader data when user logs in and we have an error
    useEffect(() => {
        if (!pending && user && user._id && !ok && !hasRevalidated.current) {
            hasRevalidated.current = true;
            revalidator.revalidate();
        }
    }, [user, pending, ok, revalidator]);

    // Reset hasRevalidated when data loads successfully
    useEffect(() => {
        if (ok) {
            hasRevalidated.current = false;
        }
    }, [ok]);

    // Show loading while revalidating
    if (pending || revalidator.state === 'loading' || (!ok && user && user._id && !hasRevalidated.current)) {
        return <Centered><div>Loading...</div></Centered>;
    }
    const wsis = items.filter(item=>item.largeImage);
    const awaitingUpload = items.filter(item=>item.awaitingUpload);
    
    if(ok){
        return (
            <Centered>
            <nav className={styles.breadcrumb} aria-label="Breadcrumb">
                <Link to="/collections">Collections</Link>
                {breadcrumb.map((b) => (
                    <span key={b.id}>
                        <span aria-hidden="true"> / </span>
                        {b.type === 'collection' ? (
                            <Link to={`/collection/${b.id}`}>{b.name}</Link>
                        ) : (
                            <Link to={`/folder/${b.id}`}>{b.name}</Link>
                        )}
                    </span>
                ))}
                <span aria-hidden="true"> / </span>
                <span>{data.name}</span>
            </nav>
            <h2 className={styles.pageTitle}>{data.name}</h2>
            {(folders.length === 0 && wsis.length === 0) && <div className={styles.emptyHint}>Empty folder</div>}
            <div className={styles.cardList}>
                {folders.map(f => (
                    <div key={f._id} className={styles.folder}>
                        <Link to={`/folder/${f._id}`}>{f.name}</Link>
                    </div>
                ))}
            </div>
            <div className={styles.itemgrid}>
                {wsis.map(WsiCard)}
            </div>
            </Centered>
        )
    } else {
        return (
            <Centered>
            <h1>Oops, there was a problem</h1>
            <p>{error}</p>
            </Centered>
        )
    }
    
}

/**
 * Build breadcrumb from folder up to collection (root).
 */
async function buildFolderBreadcrumb(folderData) {
    const crumb = [];
    let current = folderData;
    while (current && current.parentId) {
        const parentId = current.parentId;
        const parentType = current.parentType || current.parentCollection || 'folder';
        try {
            const res = parentType === 'collection'
                ? await api.get(`collection/${parentId}`)
                : await api.get(`folder/${parentId}`);
            const parent = extractData(res);
            if (!parent) break;
            crumb.push({ id: parent._id, name: parent.name, type: parentType });
            current = parent;
        } catch {
            break;
        }
    }
    crumb.reverse();
    return crumb;
}

/**
 * Loader for folder page
 * Uses React Query to prefetch and cache data
 */
async function Loader({params}){
    const { folderId } = params;
    
    const [folderResponse, itemsResponse, foldersResponse] = await Promise.all([
        fetchApiQuery(queryClient, {
            queryKey: ['folder', folderId],
            queryFn: () => api.get(`folder/${folderId}`),
            staleTime: 5 * 60 * 1000,
        }).catch(() => null),
        fetchApiQuery(queryClient, {
            queryKey: ['items', 'folder', folderId],
            queryFn: () => api.get(`item`, {query: {'folderId': folderId}}),
            staleTime: 5 * 60 * 1000,
        }).catch(() => null),
        fetchApiQuery(queryClient, {
            queryKey: ['folders', 'parent', folderId],
            queryFn: () => api.get(`folder`, {query: {'parentId': folderId, 'parentType': 'folder'}}),
            staleTime: 5 * 60 * 1000,
        }).catch(() => null),
    ]);

    const folderData = extractData(folderResponse);
    const items = extractData(itemsResponse);
    const folders = extractData(foldersResponse);

    if (!folderData) {
        return {
            ok: false,
            data: null,
            error: 'There was a problem contacting the DSA server.'
        };
    }

    const breadcrumb = await buildFolderBreadcrumb(folderData);

    return {
        ok: true,
        data: folderData,
        meta: folderData.meta,
        items: items || [],
        folders: folders || [],
        breadcrumb,
        error: (!items || !folders) ? "There was a problem fetching items for this folder" : null
    };
}
export default Folder;
export { Folder, Loader };